import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { CurrencyProvider } from './context/CurrencyContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SiteLockGate from './components/SiteLockGate';
import CurrencyWidget from './components/CurrencyWidget';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import OtpVerify from './pages/OtpVerify';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import OrderHistory from './pages/OrderHistory';
import Profile from './pages/Profile';
import AdminDashboard from './pages/AdminDashboard';
import ProtectedRoute from './components/ProtectedRoute';
import Checkout from './pages/Checkout';
import PaymentCallback from './pages/PaymentCallback';
import OrderSuccess from './pages/OrderSuccess';
import PageTracker from './components/PageTracker';
import NewsletterPopup from './components/NewsletterPopup';
import PasswordAccess from './pages/PasswordAccess';
import OwnerLogin from './pages/OwnerLogin';
import axios from 'axios';
import { useEffect, useState } from 'react';
import TrackOrder from './pages/TrackOrder';




function AppContent() {
  const location = useLocation();
  const { user } = useAuth();

  // ---------- Site lock state ----------
  const [locked, setLocked] = useState(false);
  const [lockLoading, setLockLoading] = useState(true);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/site-lock/status`)
      .then(res => setLocked(res.data.locked))
      .catch(() => {})
      .finally(() => setLockLoading(false));
  }, []);

  // Pages that should NEVER show the lock screen
  const isPublicPage =
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname === '/password-access' ||
    location.pathname === '/owner-login' ||
    location.pathname.startsWith('/admin');

  const siteToken = sessionStorage.getItem('siteAccessToken');
  const adminOrOwner = user &&
    (user.role?.toLowerCase() === 'admin' || user.role?.toLowerCase() === 'owner');

  const shouldLock = locked && !isPublicPage && !siteToken && !adminOrOwner;

  const hideNavAndFooter =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password' ||
    location.pathname === '/verify-otp' ||
    location.pathname === '/password-access' ||
    location.pathname === '/owner-login' ||
    shouldLock;

  const isHome =
    location.pathname === '/' ||
    location.pathname === '/password-access' ||
    location.pathname === '/owner-login';

  // ---------- Axios: Request interceptor (adds token) ----------
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(config => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  // ---------- Axios: Response interceptor (handles 401 globally) ----------
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // avoid redirect loop if already on login page
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  if (lockLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin h-8 w-8 border-4 border-white border-t-transparent rounded-full" />
      </div>
    );
  }

  // Pages where the currency converter should appear
  const showCurrencyWidget = ['/products', '/cart', '/checkout', '/orders']
    .some(path => location.pathname.startsWith(path));

  return (
    <>
      {!hideNavAndFooter && <Navbar />}
      <SiteLockGate showLock={shouldLock}>
        <div className={isHome ? '' : 'pt-16 lg:pt-20'}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-otp" element={<OtpVerify />} />
            <Route path="/password-access" element={<PasswordAccess />} />
            <Route path="/owner-login" element={<OwnerLogin />} />
            <Route path="/products" element={<Products />} />
            <Route path="/payment/callback" element={<PaymentCallback />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/order-confirmation/:orderId" element={<OrderSuccess />} />
            <Route path="/track/:trackingNumber" element={<TrackOrder />} />
            <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
            <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
            <Route path="/order-success" element={<ProtectedRoute><OrderSuccess /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><OrderHistory /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/admin/*" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
          </Routes>
        </div>
      </SiteLockGate>
      {!hideNavAndFooter && <Footer />}
      {showCurrencyWidget && <CurrencyWidget />}
      <NewsletterPopup />
      <PageTracker />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </CurrencyProvider>
      </CartProvider>
    </AuthProvider>
  );
}